import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import BackendService from '../services/backendService';
import useAiAssessedScores from './useAiAssessedScores';

const useListAllAiAssessedScores = ({
  gameId,
  tenantId,
  slideId,
  isUsingSockets,
  setShowMediaIsDisabled,
}: {
  gameId: string;
  tenantId: string;
  slideId?: string;
  isUsingSockets: boolean;
  setShowMediaIsDisabled?: (val: boolean) => void;
}) => {
  const { gameAPI } = useContext(SocketContext);

  const scoresCacheKey = ['aiAssessedScores', gameId, slideId];
  const { data, isFetching: isFetchingAiAssessedScores } = useQuery(
    scoresCacheKey,
    async () => {
      if (isUsingSockets) {
        if (!gameAPI || !slideId) return Promise.resolve(undefined);
        const results = await gameAPI.game.listAllAiAssessments({
          gameId,
          slideId,
          tenantId,
        });
        return results.results;
      } else {
        //if if is not during the live game they are viewing the results - don't use sockets
        if (!slideId) return Promise.resolve(undefined);
        const res = await BackendService.instance.game.retrievePublicAiFeedback({
          gameId,
          slideId,
        });
        if (!res) return Promise.resolve(undefined);
        return res.results;
      }
    },
    {
      enabled:
        Boolean(isUsingSockets && gameAPI && slideId) ||
        Boolean(!isUsingSockets && slideId),
      staleTime: 1000 * 60 * 5,
      retry: false,
    },
  );

  const {
    generateAllAiAssessedScores,
    isFetchingAiAssessedScores: isGeneratingNewScores,
    aiAssessedScores: newAiAssessedScores,
  } = useAiAssessedScores({
    gameId,
    slideId: slideId || '',
    tenantId,
    setShowMediaIsDisabled,
  });

  useEffect(() => {
    if (
      isUsingSockets &&
      (!data || (data && data.length === 0)) &&
      !isFetchingAiAssessedScores &&
      setShowMediaIsDisabled
    ) {
      // only generate new scores if playing the live game
      // This is a huge hack. We need to solve it properly in the backend some time in the future.
      // Currently the solution is to prevent the user from doing a full rerender of the response grid result component.
      // Doing so will trigger a new request to the backend to generate new AI scores.
      // Disabling the show media button will prevent a user from doing a full rerender of the response grid result component.
      setShowMediaIsDisabled(true);
      generateAllAiAssessedScores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingAiAssessedScores, isUsingSockets, setShowMediaIsDisabled]);

  return {
    aiAssessedScores: data || newAiAssessedScores,
    isGeneratingNewScores,
    isFetchingAiAssessedScores,
  };
};

export default useListAllAiAssessedScores;
